import React from 'react';
import type {FrameSizeTransitionControlHandle} from '../FrameSizeTransitionStore/useFrameSizeTransition';
import {getFrameDimensions} from '../FrameDomUtils';
import {useFrameResizeEventHandlers} from './FrameResizeHandle';

/**
 * Without adding a delay when ending the transition, a noticeable flashing
 * will be seen when dragging is finished. This is because the transition
 * clears before the Flux store finished updating. Using `SyncLane` alone
 * does not prevent this flashing, and we should create a better
 * sync-to-flux mechanism.
 * TODO: https://webflow.atlassian.net/browse/STRUCT-3308
 */
const COMMIT_FRAME_RESIZE_TO_FLUX_DELAY = 100;

type UseFrameResizeCallbacksArgs = {
  frameId: string;
  getViewportScale: () => number;
  frameSizeTransitionControl: FrameSizeTransitionControlHandle;
  onDragStartFailure?: () => void;
  inheritingFrameIds?: (
    frameId: string,
    dimension: 'width' | 'height'
  ) => string[];
  onCommit: (dimension: 'width' | 'height', value: number) => void;
  /**
   * Optional clamping function applied during drag updates and on commit.
   * Use this to enforce min/max constraints (e.g. breakpoint width bounds).
   */
  clampValue?: (dimension: 'width' | 'height', value: number) => number;
};

/**
 * Shared resize choreography for frame resize handles.
 * Owns the start → update → end lifecycle of `frameSizeTransitionControl`,
 * delegating commit logic and optional side effects to the caller.
 *
 * Used by both ComponentCanvasFrame and MCCFrame.
 */
export const useFrameResizeCallbacks = ({
  frameId,
  getViewportScale,
  frameSizeTransitionControl,
  onDragStartFailure,
  inheritingFrameIds,
  onCommit,
  clampValue,
}: UseFrameResizeCallbacksArgs) => {
  // Unclamped cumulative drag position. Without this, deltas accumulate onto
  // the clamped value and reversing past a clamp bound resizes prematurely.
  const unclampedDragValueRef = React.useRef<{
    width?: number;
    height?: number;
  }>({});

  const onResized = React.useCallback(
    async (
      delta: number,
      dimension: 'width' | 'height',
      eventType?: 'drag-start' | 'drag'
    ) => {
      const deltaInWorldUnits = Math.round(delta / getViewportScale());

      if (eventType === 'drag') {
        const currentUnclamped = unclampedDragValueRef.current[dimension];
        if (currentUnclamped === undefined) return;

        const nextUnclamped = currentUnclamped + deltaInWorldUnits;
        unclampedDragValueRef.current[dimension] = nextUnclamped;

        frameSizeTransitionControl.update((currentValue) => {
          if (currentValue[dimension] === undefined) return currentValue;
          const nextValue = clampValue
            ? clampValue(dimension, nextUnclamped)
            : nextUnclamped;
          return {
            ...currentValue,
            [dimension]: nextValue,
          };
        });
      } else if (eventType === 'drag-start') {
        const dims = await getFrameDimensions(frameId);
        if (!dims) {
          onDragStartFailure?.();
          return;
        }

        unclampedDragValueRef.current[dimension] = dims[dimension];

        const ids = inheritingFrameIds?.(frameId, dimension);
        frameSizeTransitionControl.start(
          {[dimension]: dims[dimension]},
          ids ?? []
        );
      }
    },
    [
      frameId,
      getViewportScale,
      frameSizeTransitionControl,
      onDragStartFailure,
      inheritingFrameIds,
      clampValue,
    ]
  );

  const {onFrameHandleDrag} = useFrameResizeEventHandlers({onResized});

  const onFrameHandleDragEnd = React.useCallback(
    (dimension: 'width' | 'height') => {
      frameSizeTransitionControl.end(
        (finalTransitionValue) => {
          const value = finalTransitionValue && finalTransitionValue[dimension];
          if (typeof value !== 'number') return;
          const committedValue = clampValue
            ? clampValue(dimension, value)
            : value;
          onCommit(dimension, committedValue);
        },
        {delay: COMMIT_FRAME_RESIZE_TO_FLUX_DELAY}
      );
    },
    [frameSizeTransitionControl, onCommit, clampValue]
  );

  return {onFrameHandleDrag, onFrameHandleDragEnd};
};
